import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import AddressAutocomplete from "../components/AddressAutocomplete";
import TriageQuestions, { evaluateTriage, SEV_RANK } from "../components/resident/TriageQuestions";
import SlotPicker from "../components/resident/SlotPicker";
import { Buildings, Warning, CheckCircle, Camera, X, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const RESIDENT_TOKEN_KEY = "resident_token";

const SEVERITIES = [
  { id: "critical", label: "Critical", color: "#dc2626", bg: "bg-red-50 dark:bg-red-950/30", border: "border-red-300 dark:border-red-700" },
  { id: "urgent", label: "Urgent", color: "#d97706", bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-300 dark:border-amber-700" },
  { id: "non_urgent", label: "Non-urgent", color: "#2563EB", bg: "bg-blue-50 dark:bg-blue-950/30", border: "border-blue-300 dark:border-blue-700" },
];

export default function TenantReportPublicPage() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const [tenantName, setTenantName] = useState("");
  const [tenantEmail, setTenantEmail] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [townCity, setTownCity] = useState("");
  const [county, setCounty] = useState("");
  const [postcode, setPostcode] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [customFieldVals, setCustomFieldVals] = useState({});
  const [files, setFiles] = useState([]);
  const [triageAnswers, setTriageAnswers] = useState({});
  const [slots, setSlots] = useState([]);
  const [severityTouched, setSeverityTouched] = useState(false);
  const [resident, setResident] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/tenant-report/form/${token}`);
        setForm(res.data);
        if (res.data.categories?.length) setCategory(res.data.categories[0]);
      } catch (e) {
        setForm({ error: e.response?.data?.detail || "Form not found" });
      } finally {
        setLoading(false);
      }
      const rt = localStorage.getItem(RESIDENT_TOKEN_KEY);
      if (rt) {
        try {
          const me = await axios.get(`${BACKEND}/api/tenant-report/me`, { headers: { "X-Resident-Token": rt } });
          setResident(me.data);
          if (me.data.name) setTenantName(me.data.name);
          if (me.data.email) setTenantEmail(me.data.email);
          if (me.data.phone) setTenantPhone(me.data.phone);
          const a = me.data.last_address || {};
          if (a.street_address) { setStreetAddress(a.street_address); setTownCity(a.town_city || ""); setCounty(a.county || ""); setPostcode(a.postcode || ""); setLatitude(a.latitude || null); setLongitude(a.longitude || null); }
        } catch { localStorage.removeItem(RESIDENT_TOKEN_KEY); }
      }
    })();
  }, [token]);

  const accent = form?.branding?.accent_color || "#1D4ED8";
  const triageQs = form?.triage?.[category] || [];
  const triage = evaluateTriage(triageQs, triageAnswers);

  // Suggested urgency follows the triage answers until the resident picks one themselves.
  useEffect(() => {
    if (!severityTouched && triageQs.length && triage.answered > 0) setSeverity(triage.suggested);
  }, [triage.suggested, triage.answered, triageQs.length, severityTouched]);
  useEffect(() => { setTriageAnswers({}); setSeverityTouched(false); }, [category]);

  const onPickFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const ok = incoming.filter(f => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (ok.length !== incoming.length) toast.error("Only images and videos are allowed");
    setFiles(prev => [...prev, ...ok].slice(0, 10));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!tenantName || !tenantPhone || !title || !description || !severity) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.mode !== "property" && (!streetAddress || !postcode)) {
      toast.error("Please fill in your address");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("tenant_name", tenantName);
      fd.append("tenant_email", tenantEmail);
      fd.append("tenant_phone", tenantPhone);
      fd.append("notify_whatsapp", notifyWhatsapp ? "1" : "0");
      fd.append("title", title);
      fd.append("description", description);
      fd.append("category", category);
      fd.append("severity", severity);
      if (form.mode !== "property") {
        fd.append("street_address", streetAddress);
        fd.append("town_city", townCity);
        fd.append("county", county);
        fd.append("postcode", postcode);
        if (latitude) fd.append("latitude", latitude);
        if (longitude) fd.append("longitude", longitude);
      }
      Object.entries(customFieldVals).forEach(([k, v]) => fd.append(`cf_${k}`, v));
      fd.append("triage_answers", JSON.stringify(triageAnswers));
      fd.append("preferred_slots", JSON.stringify(severity === "critical" ? [] : slots));
      files.forEach(f => fd.append("files", f));
      const res = await axios.post(`${BACKEND}/api/tenant-report/form/${token}/submit`, fd);
      setSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!form || form.error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center">
          <Warning className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-2xl font-semibold mb-2">Form unavailable</h1>
          <p className="text-sm text-slate-500">{form?.error || "This report form is not active."}</p>
        </div>
      </div>
    );
  }

  if (success) {
    const trackUrl = `${window.location.origin}/report-status/${success.tracking_token}`;
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 text-center" data-testid="trp-success">
          <div className="h-16 w-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-emerald-600" weight="fill" />
          </div>
          <h1 className="text-2xl font-bold mb-1">Report received</h1>
          <p className="text-sm text-slate-500 mb-5">Our team has been notified and will respond as quickly as possible.</p>
          <div className="bg-muted/40 rounded-lg p-4 text-left space-y-2 mb-5">
            <p className="text-xs text-slate-500">Reference</p>
            <p className="font-mono text-lg font-semibold" data-testid="trp-success-ref">{success.reference}</p>
          </div>
          <Link to={`/report-status/${success.tracking_token}`}>
            <Button className="w-full" style={{ backgroundColor: accent }} data-testid="trp-track-btn">Track this report</Button>
          </Link>
          {form.resident_login_enabled && tenantEmail && (
            <Link to="/resident" className="block mt-2 text-xs underline text-slate-500" data-testid="trp-success-resident-link">Sign in with {tenantEmail} to see all your reports, add photos and message us</Link>
          )}
          <p className="text-[11px] text-slate-400 mt-3 break-all">Save this link: {trackUrl}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-6" data-testid="trp-public-page">
      <div className="max-w-2xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="text-center pt-2">
          {form.branding?.logo_url ? (
            <img src={form.branding.logo_url} alt="Logo" className="h-12 mx-auto mb-3" />
          ) : (
            <div className="h-12 w-12 rounded-md flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: accent }}>
              <Buildings className="h-6 w-6 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Report a Maintenance Issue</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{form.client_company}</p>
          {form.welcome_text && (
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-3 max-w-md mx-auto">{form.welcome_text}</p>
          )}
          {form.resident_login_enabled && (
            <p className="mt-3 text-xs" data-testid="trp-resident-bar">
              {resident ? (
                <Link to="/resident" className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300 underline"><UserCircle className="h-4 w-4" /> Signed in as {resident.email} — view my reports</Link>
              ) : (
                <Link to="/resident" className="inline-flex items-center gap-1 text-slate-500 underline" data-testid="trp-resident-signin"><UserCircle className="h-4 w-4" /> Reported before? Sign in to track all your reports</Link>
              )}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-5 bg-card border border-border rounded-xl p-5">
          {/* Reporter details */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Your details</h2>
            <div><Label>Your name *</Label><Input value={tenantName} onChange={e => setTenantName(e.target.value)} required className="mt-1" data-testid="trp-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Phone *</Label><Input type="tel" value={tenantPhone} onChange={e => setTenantPhone(e.target.value)} required placeholder="+44 7…" className="mt-1" data-testid="trp-phone" /></div>
              <div><Label>Email (optional)</Label><Input type="email" value={tenantEmail} onChange={e => setTenantEmail(e.target.value)} placeholder="for status updates" className="mt-1" data-testid="trp-email" /></div>
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none p-3 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
              <input type="checkbox" checked={notifyWhatsapp} onChange={e => setNotifyWhatsapp(e.target.checked)}
                     className="mt-0.5" data-testid="trp-whatsapp-optin" />
              <span>
                <strong className="text-emerald-700 dark:text-emerald-300">WhatsApp me updates</strong> on the phone number above when work is scheduled or an engineer acknowledges (recommended for fastest updates — no app required).
              </span>
            </label>
          </div>

          {/* Address */}
          {form.mode === "property" ? (
            <div className="space-y-2">
              <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Property</h2>
              <p className="text-sm text-slate-700 dark:text-slate-200 bg-muted/30 rounded-md p-3" data-testid="trp-prop-fixed">📍 {form.property_address}</p>
            </div>
          ) : (
            <div className="space-y-3">
              <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Property address</h2>
              <AddressAutocomplete
                label="Search address"
                placeholder="Start typing postcode or address…"
                onSelect={(a) => {
                  if (a.street_address) setStreetAddress(a.street_address);
                  if (a.town_city) setTownCity(a.town_city);
                  if (a.county) setCounty(a.county);
                  if (a.postcode) setPostcode(a.postcode);
                  if (a.latitude) setLatitude(a.latitude);
                  if (a.longitude) setLongitude(a.longitude);
                }}
                data-testid="trp-address-autocomplete"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label>Street *</Label><Input value={streetAddress} onChange={e => setStreetAddress(e.target.value)} required className="mt-1" data-testid="trp-street" /></div>
                <div><Label>Town / City</Label><Input value={townCity} onChange={e => setTownCity(e.target.value)} className="mt-1" data-testid="trp-town" /></div>
                <div><Label>County</Label><Input value={county} onChange={e => setCounty(e.target.value)} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label>Postcode *</Label><Input value={postcode} onChange={e => setPostcode(e.target.value)} required className="mt-1" data-testid="trp-postcode" /></div>
              </div>
            </div>
          )}

          {/* Issue details */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Issue details</h2>
            <div>
              <Label>Type of issue *</Label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                      className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                      data-testid="trp-category">
                {form.categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Short title *</Label><Input value={title} onChange={e => setTitle(e.target.value)} required placeholder="e.g. Boiler not heating water" className="mt-1" data-testid="trp-title" /></div>
            <div><Label>Describe the issue *</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} required rows={4} className="mt-1" placeholder="When did it start? What have you tried? Anything we should know?" data-testid="trp-desc" /></div>
          </div>

          {/* Guided triage for the chosen category */}
          <TriageQuestions questions={triageQs} answers={triageAnswers} onChange={setTriageAnswers} accent={accent} />

          {/* Severity */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">How severe is it? *</h2>
            {triageQs.length > 0 && triage.answered > 0 && (
              <p className="text-xs rounded-md px-3 py-2 bg-muted/40 border border-border" data-testid="trp-suggested-severity">
                Based on your answers we suggest <strong>{SEVERITIES.find(s => s.id === triage.suggested)?.label}</strong>{severityTouched && severity !== triage.suggested ? " — you've chosen differently, that's fine." : "."}
                {!severityTouched && SEV_RANK[triage.suggested] < 2 && " You can change this if you disagree."}
              </p>
            )}
            <div className="space-y-2">
              {SEVERITIES.map(s => {
                const helper = form.severity_copy?.[s.id] || "";
                const selected = severity === s.id;
                return (
                  <button type="button" key={s.id}
                          onClick={() => { setSeverity(s.id); setSeverityTouched(true); }}
                          className={`w-full text-left border-2 rounded-lg p-3 transition ${selected ? `${s.bg} ${s.border}` : "border-border hover:bg-muted/30"}`}
                          data-testid={`trp-sev-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="h-4 w-4 rounded-full border-2" style={{ borderColor: s.color, backgroundColor: selected ? s.color : "transparent" }} />
                      <div className="flex-1">
                        <p className="font-semibold text-sm" style={{ color: s.color }}>{s.label}</p>
                        {helper && <p className="text-[12px] text-slate-600 dark:text-slate-300 mt-0.5 leading-snug">{helper}</p>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {severity === "critical" && (
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-700 rounded-md p-3 flex items-start gap-2" data-testid="trp-critical-warning">
                <Warning className="h-4 w-4 text-red-600 shrink-0 mt-0.5" weight="fill" />
                <p className="text-xs text-red-800 dark:text-red-300">
                  ⚠️ <strong>Critical means real risk to life or property right now.</strong> If this is gas/fire/major flooding, also call emergency services. We will treat this as an emergency call-out.
                </p>
              </div>
            )}
          </div>

          {/* Custom fields */}
          {(form.custom_fields || []).length > 0 && (
            <div className="space-y-3">
              <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Additional info</h2>
              {form.custom_fields.map((cf, i) => (
                <div key={i}>
                  <Label>{cf.label}{cf.required ? " *" : ""}</Label>
                  {cf.type === "textarea" ? (
                    <Textarea value={customFieldVals[cf.label] || ""} onChange={e => setCustomFieldVals({ ...customFieldVals, [cf.label]: e.target.value })}
                              required={cf.required} rows={2} className="mt-1" data-testid={`trp-cf-${i}`} />
                  ) : cf.type === "select" ? (
                    <select value={customFieldVals[cf.label] || ""} onChange={e => setCustomFieldVals({ ...customFieldVals, [cf.label]: e.target.value })}
                            required={cf.required}
                            className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                            data-testid={`trp-cf-${i}`}>
                      <option value="">Select…</option>
                      {(cf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input value={customFieldVals[cf.label] || ""} onChange={e => setCustomFieldVals({ ...customFieldVals, [cf.label]: e.target.value })}
                           required={cf.required} className="mt-1" data-testid={`trp-cf-${i}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Photos / videos */}
          <div className="space-y-3">
            <h2 className="text-xs font-semibold tracking-wider text-slate-500 uppercase">Photos / videos {triage.answered > 0 ? "— really helps us bring the right parts" : "(optional)"}</h2>
            <p className="text-xs text-slate-500">Up to 10 files. Photos help us understand the issue faster.</p>
            <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple onChange={onPickFiles}
                   className="hidden" data-testid="trp-file-input" />
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="trp-add-files-btn">
              <Camera className="h-4 w-4 mr-2" /> Add photos / videos
            </Button>
            {files.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {files.map((f, i) => (
                  <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-border bg-muted/30">
                    {f.type.startsWith("image/") ? (
                      <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[10px] p-1 text-center">
                        <Camera className="h-5 w-5 mb-1" />
                        <span className="truncate w-full">{f.name}</span>
                      </div>
                    )}
                    <button type="button" onClick={() => removeFile(i)}
                            className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-black"
                            data-testid={`trp-file-remove-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <SlotSection form={form} severity={severity} slots={slots} setSlots={setSlots} accent={accent} />

          <Button type="submit" className="w-full text-base py-6" disabled={submitting}
                  style={{ backgroundColor: accent }} data-testid="trp-submit-btn">
            {submitting ? "Submitting…" : "Submit report"}
          </Button>
        </form>

        <p className="text-[11px] text-center text-slate-400 pb-4">Powered by Facilities Management Platform</p>
      </div>
    </div>
  );
}

/* Appointment preferences sit between photos and submit; critical issues skip them (we're coming now). */
export function SlotSection({ form, severity, slots, setSlots, accent }) {
  if (!form.slots?.length || severity === "critical") return null;
  return <SlotPicker slots={form.slots} value={slots} onChange={setSlots} accent={accent} />;
}
